<template>
  <div class="file-system-container modal-overflow">
    <breadcrumbs v-if="folderPath" :path="folderPath" @click="breadcrumbs"></breadcrumbs>
    <hr />
    <div v-for="folder in fileSystem.folders" style="margin-top: 15px;">
      <div>
        <span class="folder-icon"><a href="#" @click.prevent="fetchDirectoryContents(folder)"><img src="static/img/file-mgr/folder.png" /></a></span>
        <a href="#" @click.prevent="fetchDirectoryContents(folder)">{{folder}}</a>
      </div>
    </div>
    <div class="one-file" v-for="file in fileSystem.files" @mouseover="showDeleteAndDlPdf(file)" @mouseleave="hideDeleteAndDlPdf(file)">
      <span class="file-icon"><a href="#" @click.prevent="downloadFileOrFlatPdf(file)"><img :src="'static/img/file-mgr/' + icon(file)" /></a></span>
      <a href="#" @click.prevent="downloadFileOrFlatPdf(file)">{{file}}</a>
      <button :ref="`openFile${file}`" class="dlPdfButton" @click.prevent="downloadFile(file, true)" style="padding-left: 7px; margin-right: -4px; visibility: hidden" title="Open File"><img src="static/img/file-mgr/open.png" /></button>
      <button :ref="`downloadPdf${file}`" class="dlPdfButton" @click.prevent="downloadFile(file)" style="visibility: hidden" title="Download Editable PDF"><img src="static/img/file-mgr/dl-editable-pdf.png" /></button>
      <span :ref="file" class="delete-icon" v-if="canEdit" style="visibility: hidden"><a href="#" @click.prevent="confirmDeleteFile(file)"><img src="static/img/file-mgr/delete.png" title="Delete File"/></a></span>
    </div>
    <div v-if="fileSystem.folders.length === 0 && fileSystem.files.length === 0">
      No files or folders found.
    </div>
    <button v-if="isJobFolder && isJobIdSet" type="button" @click="addTaskFolders" class="btn btn-sm btn-fill btn-info" style="margin-top: 20px;">
      Add Task Folders
    </button>
    <hr />
    <div v-if="canEdit">
      <div class="row">
        <div class="col-md-12" style="padding-top: 18px;">
          <input id="files" type="file" name="files" @change="fileUpload" ref="fileUpload" multiple="multiple" v-validate="modelValidations.fileUpload" />
          <span v-if="totalSizeInBytes > 0">{{ fileSize }}</span>
          <small class="text-danger" style="display: block; margin-top: 5px;" v-show="tooLongFilenames.length > 0">
            The following filenames are too long ( > {{ MAX_FILENAME_LENGTH }}chars ):<br/><br/>
              <span v-for="filename in tooLongFilenames" style="color: #5E5E5C">{{filename}}<br/><br/></span>
            Please shorten filenames before uploading.
          </small>
        </div>
      </div>
      <div class="row">
        <div class="col-md-12 d-flex justify-content-end">
          <p-checkbox v-model="useS3FileUpload" v-if="isS3UploadEnabled" :disabled="isDevEnvironment" style="margin-top: 15px; margin-right: 15px;">S3 Upload (recommended for large files)</p-checkbox>
          <button @click="validateAndSubmit" class="btn btn-fill btn-info btn-wd btn-save" :disabled="isSaving || isInitial">Upload File</button>
        </div>
      </div>
      <div class="row" v-if="useS3FileUpload && !isSaving">
        <div class="col-md-12"><strong>NOTE:</strong> When using the S3 upload, there will be a slight delay for files to show up here after the upload completes.</div>
      </div>
      <div class="upload-progress" v-if="isSaving">
        <p-progress :value="uploadProgress" type="info"></p-progress>
        <p style="margin-top: 12px;" v-if="currentlyUploading">Currently uploading <strong>{{ currentlyUploading }}</strong>.</p>
      </div>
    </div>
  </div>
</template>

<script>
  import {mapGetters} from 'vuex'
  import {formatFileSize, icon, notifyError, notifyFileUploads, notifySuccess} from 'src/utilities/utils'
  import PProgress from 'src/components/UIComponents/Progress.vue'
  import Breadcrumbs from 'src/components/Dashboard/Views/Files/Modal/Breadcrumbs.vue'
  import Constants from 'src/utilities/constants'
  import Permissions from 'src/utilities/permissions'
  import {ACCEPTED, NO_CONTENT, OK, UNAUTHORIZED} from 'http-status-codes'
  import constants from 'src/utilities/constants'

  const STATUS_INITIAL = 0
  const STATUS_SAVING = 1
  const STATUS_SUCCESS = 2
  const STATUS_FAILED = 3
  const STATUS_READY_FOR_UPLOAD = 5

  const MAX_FILENAME_LENGTH = 100;

  export default{
    props: ['data'],
    components: {
      PProgress,
      Breadcrumbs
    },
    computed: {
      ...mapGetters({
        fileName: 'files/fileName',
        fileSystem: 'files/fileSystem',
        uploadProgress: 'files/uploadProgress',
        currentlyUploading: 'files/nameOfFileCurrentlyUploading'
      }),
      canEdit() {
        return this.$can('edit', Permissions.FILES)
      },
      isInitial() {
        return this.currentStatus === STATUS_INITIAL
      },
      isSaving() {
        return this.currentStatus === STATUS_SAVING
      },
      isJobIdSet() {
        return !!this.$store.getters['files/jobId']
      },
      isJobFolder() {
        if (!this.folderPath) {
          return false
        }
        const parts = this.folderPath.split('/')
        const numFoldersToJob = 5
        return parts.length === numFoldersToJob
      },
      fileSize() {
        return formatFileSize(this.totalSizeInBytes)
      },
      isDevEnvironment() {
        return Constants.ENVIRONMENTS.DEVELOPMENT === process.env.NODE_ENV
      },
      isS3UploadEnabled() {
        return process.env.VUE_APP_ENABLE_UPLOADS_AND_DOWNLOADS_VIA_S3 === 'true'
      }
    },
    data() {
      return {
        toDelete: null,
        overwrite: false,
        formName: null,
        currentStatus: null,
        folderPath: null,
        selectedFiles: [],
        tooLongFilenames: [],
        totalSizeInBytes: 0,
        useS3FileUpload: false,
        useDirectFileUploadAndDownload: true, // no longer need to hop through s3 - direct to file manager api
        MAX_FILENAME_LENGTH: MAX_FILENAME_LENGTH,
        modelValidations: {
          fileUpload: { // TODO get validation working on file upload
            required: true
          }
        }
      }
    },
    methods: {
      breadcrumbs(path) {
        this.$store.commit('files/jobId', null)
        this.$store.commit('files/jobPath', null)
        this.folderPath = path
      },
      addTaskFolders() {
        const payload = {
          jobId: this.$store.getters['files/jobId'],
          jobPath: this.$store.getters['files/jobPath']
        }

        this.$eventHub.$emit('show-modal-2', 'Create Task Folders', payload, '800px')
      },
      downloadFileOrFlatPdf(fileName) {
        if (fileName.endsWith(".pdf")) {
          this.$store.dispatch('files/downloadFlatPdf', {
            name: fileName,
            path: this.folderPath
          })
          return
        }
        this.downloadFile(fileName, false, this.useDirectFileUploadAndDownload)
      },
      downloadFile(fileName, openFile = false, useDirectFileUploadAndDownload = false) {
        const payload = {
          name: fileName,
          path: this.folderPath,
          openFile,
          useDirectFileUploadAndDownload
        }

        this.$store.dispatch('files/download', payload)
      },
      showDeleteAndDlPdf(file) {
        this.$refs[file][0].style.visibility = 'visible'
        this.$refs[`openFile${file}`][0].style.visibility = 'visible'
        if (file.endsWith(".pdf")) {
          this.$refs[`downloadPdf${file}`][0].style.visibility = 'visible'
          return
        }
        this.$refs[`downloadPdf${file}`][0].style.display = 'none'
      },
      hideDeleteAndDlPdf(file) {
        this.$refs[file][0].style.visibility = 'hidden'
        this.$refs[`openFile${file}`][0].style.visibility = 'hidden'
        this.$refs[`downloadPdf${file}`][0].style.visibility = 'hidden'
      },
      async confirmDeleteFile(fileName) {
        let message = 'Are you sure you want to delete <strong>' + fileName + '</strong>? This will completely remove the file.'
        this.toDelete = {
          name: fileName,
          path: this.folderPath
        }
        const payload = {
          message,
          entity: {name: fileName},
          delete: this.deleteFile,
          showButton: true
        }
        this.$eventHub.$emit('show-modal-5', 'Confirm Delete', payload, '600px')
      },
      async deleteFile() {
        const response = await this.$store.dispatch('files/delete', this.toDelete)
        if (NO_CONTENT === response.status) {
          notifySuccess(this, this.toDelete.name + ' deleted successfully')
        } else {
          notifyError(this, 'error deleting ' + this.toDelete.name)
        }
      },
      icon(file) {
        return icon(file)
      },
      fileUpload(event) {
        if (event.target.files.length > 0) {
          this.selectedFiles = Object.values(event.target.files)
          this.tooLongFilenames = []
          this.totalSizeInBytes = 0

          for (const file in this.selectedFiles) {
            this.totalSizeInBytes += this.selectedFiles[file].size
            if (this.selectedFiles[file].name.length >= MAX_FILENAME_LENGTH) {
              this.tooLongFilenames.push(this.selectedFiles[file].name)
              this.currentStatus = STATUS_INITIAL
            }
          }

          if (this.tooLongFilenames.length === 0) {
            this.currentStatus = STATUS_READY_FOR_UPLOAD
          }

        } else {
          this.tooLongFilenames = []
          this.currentStatus = STATUS_INITIAL
        }
      },
      async handleDirectFileUpload(filesInput) {
        const response = await this.$store.dispatch('files/upload', {
          files: filesInput.files,
          path: this.folderPath
        })

        if (ACCEPTED === response.status) {
          notifyFileUploads(this, response.data)
          // update local state
          for (let i = 0; i < response.data.length; i++) {
            const fileUploadResponse = response.data[i]
            if (fileUploadResponse.status === constants.NOTIFY.SUCCESS) {
              this.$store.commit('files/addFile', fileUploadResponse.fileName)
            }
          }

          this.currentStatus = STATUS_SUCCESS
          this.selectedFiles = []
          this.totalSizeInBytes = 0
          this.$refs.fileUpload.value = null
        } else {
          notifyError(this, 'Failed to Upload Files')
          console.error('response.status', response.status)
          console.error('response.data', response.data)
          this.currentStatus = STATUS_FAILED
        }
      },
      async handleS3FileUpload(filesInput) {
        const responses = await this.$store.dispatch('files/uploadFilesViaS3', {
          files: filesInput.files,
          path: this.folderPath
        })

        for (const responseWrapper of responses) {
          const fileName = responseWrapper.fileName

          if (OK === responseWrapper.status) {
            notifySuccess(this, `${fileName} upload completed.`)
            setTimeout(() => {
              // NO-OP - chill for a brief moment to spread out notifications
            }, Constants.TIME.MILLIS.THIRD_SECOND)
          } else if (UNAUTHORIZED === responseWrapper.status) {
            notifyError(this, 'File uploading is not permitted for your user account')
          } else {
            notifyError(this, `Failed to upload ${fileName}`)
          }
        }
        // upload happens asynchronously - won't know about failures until after the fact
        this.currentStatus = STATUS_SUCCESS
        filesInput.value = null
        this.$store.set('files/uploadProgress', 0)
        this.$emit('close')
      },
      async validateAndSubmit() {
        this.currentStatus = STATUS_SAVING
        let filesInput = document.querySelector('#files')

        if (await this.$validator.validateAll()) {
          if (this.isS3UploadEnabled && this.useS3FileUpload) {
            await this.handleS3FileUpload(filesInput)
          } else {
            await this.handleDirectFileUpload(filesInput)
          }
        }
      },
      async fetchDirectoryContents(folder) {
        this.folderPath += '/' + folder
        await this.$store.dispatch('files/fetchDirectoryContents', this.folderPath)
      },
      getError(fieldName) {
        return this.errors.first(fieldName)
      }
    },
    mounted() {
      this.folderPath = this.data
      this.$store.dispatch('files/fetchDirectoryContents', this.folderPath)
      this.currentStatus = STATUS_INITIAL
    },
    destroyed() {
      this.$store.commit('files/fileName', null)
      this.$store.commit('files/jobId', null)
      this.$store.commit('files/jobPath', null)
      this.$store.commit('files/resetFileSystem', null)
    }
  }
</script>

<style scoped>
  .file-icon, .folder-icon {
    padding-right: 5px;
  }
  .delete-icon {
    padding-left: 5px;
  }
  .one-file {
    padding-bottom: 5px;
  }
  .dlPdfButton {
    border: none;
    background: transparent;
    cursor: pointer
  }
  .dlPdfButton:focus {
    outline: 0;
  }
</style>
